import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { http, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  CalendarRange, Plus, X, Users, Share2, Globe, Lock, Link as LinkIcon,
  UserPlus, Check, UserMinus, Search, Loader2, Trash2, Waves,
} from "lucide-react";

const VIS_META = {
  public:  { label: "Public",       icon: Globe,    desc: "Anyone in the community can see and join" },
  friends: { label: "Friends only", icon: Lock,     desc: "Only your friends and people you invite" },
  link:    { label: "Link only",    icon: LinkIcon, desc: "Anyone with the link can view & join" },
};

function ymd(d) { return d.toISOString().slice(0, 10); }
function fmtPlanned(iso) {
  const d = new Date(iso);
  const today = new Date();
  const tomorrow = new Date(); tomorrow.setDate(today.getDate() + 1);
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  if (sameDay(d, today)) return `Today · ${time}`;
  if (sameDay(d, tomorrow)) return `Tomorrow · ${time}`;
  return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }) + ` · ${time}`;
}

function planShareUrl(planId) {
  return `${window.location.origin}/plan/${planId}`;
}

async function sharePlan(plan) {
  const url = planShareUrl(plan.id);
  const text = `Surf with me — ${plan.beach_name}, ${fmtPlanned(plan.planned_at)}${plan.note ? `\n${plan.note}` : ""}`;
  if (navigator.share) {
    try {
      await navigator.share({ title: "Popoyo.app · Surf Plan", text, url });
      return;
    } catch (_e) { /* user dismissed */ }
  }
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Plan link copied", { description: url });
  } catch (_e) {
    toast.info("Share this link", { description: url });
  }
}

function FriendsTab({ data, refetch }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const searchQ = useQuery({
    queryKey: ["users-search", q],
    queryFn: async () => (await http.get("/users/search", { params: { q } })).data,
    enabled: q.trim().length >= 2,
    staleTime: 30_000,
  });

  const sendRequest = async (username) => {
    setBusy(true);
    try {
      const { data } = await http.post("/friends/request", { username });
      toast.success(data.status === "accepted" ? "You're now friends" : "Friend request sent");
      setQ("");
      refetch();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not send request");
    } finally { setBusy(false); }
  };

  const accept = async (id) => {
    try { await http.post(`/friends/${id}/accept`); toast.success("Friend added"); refetch(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not accept"); }
  };
  const decline = async (id) => {
    try { await http.post(`/friends/${id}/decline`); toast("Request declined"); refetch(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not decline"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this friend?")) return;
    try { await http.delete(`/friends/${id}`); toast("Friend removed"); refetch(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not remove"); }
  };

  return (
    <div className="space-y-5">
      {/* Search */}
      <div>
        <label className="eyebrow block mb-2">Find friends by username</label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--ink-2)]" />
            <Input
              data-testid="friends-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="@username"
              className="pl-9"
            />
          </div>
        </div>
        {searchQ.data && searchQ.data.length > 0 && (
          <ul className="mt-2 divide-y divide-[color:var(--ink-2)]/10 rounded-2xl bg-[color:var(--sand-2)]/50">
            {searchQ.data.map((u) => (
              <li key={u.id} data-testid={`search-result-${u.username}`} className="flex items-center justify-between px-4 py-2.5">
                <div>
                  <p className="text-sm font-semibold leading-tight">{u.name}</p>
                  <p className="text-xs text-[color:var(--ink-2)]">@{u.username}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  data-testid={`add-friend-${u.username}`}
                  onClick={() => sendRequest(u.username)}
                  className="pill"
                >
                  <UserPlus className="w-4 h-4 mr-1.5" />
                  Add
                </Button>
              </li>
            ))}
          </ul>
        )}
        {q.length >= 2 && searchQ.data && searchQ.data.length === 0 && !searchQ.isFetching && (
          <p className="text-xs text-[color:var(--ink-2)] mt-2">No users match “{q}”.</p>
        )}
      </div>

      {/* Incoming */}
      {data.incoming.length > 0 && (
        <div>
          <p className="eyebrow mb-2">Incoming requests ({data.incoming.length})</p>
          <ul className="space-y-2">
            {data.incoming.map((r) => (
              <li key={r.id} data-testid={`incoming-${r.id}`} className="flex items-center justify-between bg-[color:var(--sand-2)]/50 rounded-2xl px-4 py-2.5">
                <div>
                  <p className="text-sm font-semibold leading-tight">{r.other_user.name}</p>
                  <p className="text-xs text-[color:var(--ink-2)]">@{r.other_user.username}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" data-testid={`accept-${r.id}`} onClick={() => accept(r.id)} className="pill bg-[color:var(--navy)] text-white hover:bg-[color:var(--navy-2)]">
                    <Check className="w-4 h-4 mr-1.5" /> Accept
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`decline-${r.id}`} onClick={() => decline(r.id)} className="pill">
                    Decline
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Outgoing */}
      {data.outgoing.length > 0 && (
        <div>
          <p className="eyebrow mb-2">Pending invites you sent</p>
          <ul className="space-y-2">
            {data.outgoing.map((r) => (
              <li key={r.id} className="flex items-center justify-between bg-[color:var(--sand-2)]/50 rounded-2xl px-4 py-2.5">
                <div>
                  <p className="text-sm font-semibold leading-tight">{r.other_user.name}</p>
                  <p className="text-xs text-[color:var(--ink-2)]">@{r.other_user.username}</p>
                </div>
                <Button size="sm" variant="outline" data-testid={`cancel-${r.id}`} onClick={() => decline(r.id)} className="pill">
                  Cancel
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Friends list */}
      <div>
        <p className="eyebrow mb-2">Your friends ({data.friends.length})</p>
        {data.friends.length === 0 ? (
          <p className="text-sm text-[color:var(--ink-2)]">
            No friends yet. Search above and add a few — then they’ll see your friends-only surf plans.
          </p>
        ) : (
          <ul className="space-y-2">
            {data.friends.map((f) => (
              <li key={f.id} data-testid={`friend-${f.id}`} className="flex items-center justify-between bg-[color:var(--sand-2)]/50 rounded-2xl px-4 py-2.5">
                <div>
                  <p className="text-sm font-semibold leading-tight">{f.other_user.name}</p>
                  <p className="text-xs text-[color:var(--ink-2)]">@{f.other_user.username}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => remove(f.id)} className="text-[color:var(--ink-2)]" data-testid={`remove-${f.id}`}>
                  <UserMinus className="w-4 h-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function InviteePicker({ friends, selectedIds, onChange }) {
  if (!friends || friends.length === 0) {
    return (
      <p className="text-xs text-[color:var(--ink-2)] mt-2">
        Add friends to invite them directly. You can also share the link with anyone.
      </p>
    );
  }
  const toggle = (id) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  };
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {friends.map((f) => {
        const id = f.other_user.id;
        const on = selectedIds.includes(id);
        return (
          <button
            type="button"
            key={id}
            data-testid={`invite-${f.other_user.username}`}
            onClick={() => toggle(id)}
            className={`pill px-3 py-1.5 text-xs border transition ${on ? "bg-[color:var(--coral)] text-white border-[color:var(--coral)]" : "bg-[color:var(--sand-2)] text-[color:var(--ink)] border-[color:var(--ink-2)]/20"}`}
          >
            @{f.other_user.username}
          </button>
        );
      })}
    </div>
  );
}

function PlanComposer({ beaches, friends, onCreated, onClose }) {
  const [beachId, setBeachId] = useState(beaches?.[0]?.id || "playa-popoyo");
  const tomorrow = new Date(Date.now() + 24 * 60 * 60_000);
  tomorrow.setHours(6, 30, 0, 0);
  const [date, setDate] = useState(ymd(tomorrow));
  const [time, setTime] = useState("06:30");
  const [note, setNote] = useState("");
  const [visibility, setVisibility] = useState("friends");
  const [maxJoiners, setMaxJoiners] = useState(6);
  const [invitedIds, setInvitedIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const planned = new Date(`${date}T${time}:00`);
      const { data } = await http.post("/plans", {
        beach_id: beachId,
        planned_at: planned.toISOString(),
        note,
        visibility,
        max_joiners: Number(maxJoiners),
        invited_user_ids: invitedIds,
      });
      onCreated?.(data);
      onClose?.();
    } catch (e) {
      queueMicrotask(() => setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message));
    } finally {
      queueMicrotask(() => setBusy(false));
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[color:var(--ink)]/45 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-4" data-testid="plan-composer">
      <form onSubmit={submit} className="bg-white rounded-3xl shadow-2xl w-full sm:max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-[color:var(--ink-2)]/10">
          <h3 className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>New surf plan</h3>
          <button type="button" onClick={onClose} className="text-[color:var(--ink-2)] hover:text-[color:var(--ink)]" data-testid="close-composer">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="eyebrow block mb-1.5">Beach</label>
            <Select value={beachId} onValueChange={setBeachId}>
              <SelectTrigger data-testid="plan-beach-select" className="rounded-2xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {beaches.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.name} · {b.region}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="eyebrow block mb-1.5">Date</label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="plan-date" className="rounded-2xl" />
            </div>
            <div>
              <label className="eyebrow block mb-1.5">Time</label>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="plan-time" className="rounded-2xl" />
            </div>
          </div>
          <div>
            <label className="eyebrow block mb-1.5">Note (optional)</label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Big swell · meet at the lifeguard tower" data-testid="plan-note" className="rounded-2xl" />
          </div>
          <div>
            <label className="eyebrow block mb-1.5">Who can see this plan?</label>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(VIS_META).map(([k, m]) => {
                const Icon = m.icon;
                const active = visibility === k;
                return (
                  <button
                    type="button"
                    key={k}
                    data-testid={`vis-${k}`}
                    onClick={() => setVisibility(k)}
                    className={`rounded-2xl p-3 text-left border transition ${active ? "bg-[color:var(--navy)] text-white border-[color:var(--navy)]" : "bg-[color:var(--sand-2)] text-[color:var(--ink)] border-[color:var(--ink-2)]/15"}`}
                  >
                    <Icon className="w-4 h-4 mb-1" />
                    <p className="text-xs font-semibold">{m.label}</p>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-[color:var(--ink-2)] mt-1.5">{VIS_META[visibility].desc}</p>
          </div>
          {visibility !== "public" && (
            <div>
              <label className="eyebrow block mb-1.5">Invite friends (optional)</label>
              <InviteePicker friends={friends} selectedIds={invitedIds} onChange={setInvitedIds} />
            </div>
          )}
          <div>
            <label className="eyebrow block mb-1.5">Max joiners</label>
            <Input type="number" min={1} max={50} value={maxJoiners} onChange={(e) => setMaxJoiners(e.target.value)} data-testid="plan-max" className="rounded-2xl" />
          </div>
          {err && <p className="text-sm text-[color:var(--emergency)]" data-testid="plan-error">{err}</p>}
        </div>
        <div className="p-5 border-t border-[color:var(--ink-2)]/10 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} className="pill">Cancel</Button>
          <Button type="submit" disabled={busy} data-testid="plan-submit" className="pill bg-[color:var(--coral)] text-white hover:bg-[color:var(--coral)]/90">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
            Create plan
          </Button>
        </div>
      </form>
    </div>
  );
}

function PlanCard({ plan, currentUser, onChanged }) {
  const VisIcon = VIS_META[plan.visibility]?.icon || Globe;
  const currentUserId = currentUser?.id;
  const isHost = plan.host_id === currentUserId;
  const isAdmin = currentUser?.role === "admin";
  const isJoined = plan.joiners?.some((j) => j.id === currentUserId);
  const full = (plan.joiners?.length || 0) >= plan.max_joiners;

  const doJoin = async () => {
    try { const { data } = await http.post(`/plans/${plan.id}/join`); onChanged?.(data); toast.success("You're in"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not join"); }
  };
  const doLeave = async () => {
    try { const { data } = await http.post(`/plans/${plan.id}/leave`); onChanged?.(data); toast("You left the plan"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not leave"); }
  };
  const doDelete = async () => {
    if (!window.confirm("Delete this plan?")) return;
    try { await http.delete(`/plans/${plan.id}`); onChanged?.(null); toast("Plan deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not delete"); }
  };

  return (
    <li data-testid={`plan-${plan.id}`} className="bg-white rounded-2xl border border-[color:var(--ink-2)]/10 p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-2xl bg-[color:var(--teal)]/10 text-[color:var(--teal)] flex items-center justify-center shrink-0">
          <Waves className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-bold text-[color:var(--ink)]" style={{ fontFamily: "Outfit" }}>{plan.beach_name}</h4>
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[color:var(--ink-2)]">
              <VisIcon className="w-3 h-3" />{VIS_META[plan.visibility]?.label}
            </span>
          </div>
          <p className="text-sm text-[color:var(--ink-2)] mt-0.5">
            {fmtPlanned(plan.planned_at)} · hosted by <strong>@{plan.host_username || plan.host_name}</strong>
          </p>
          {plan.note && <p className="text-sm mt-2 text-[color:var(--ink)] whitespace-pre-line">{plan.note}</p>}
          <div className="flex items-center gap-1.5 text-xs text-[color:var(--ink-2)] mt-2 flex-wrap">
            <Users className="w-3.5 h-3.5" />
            {plan.joiners.length}/{plan.max_joiners} joined
            {plan.joiners.slice(0, 4).map((j) => (
              <span key={j.id} className="inline-flex items-center gap-1 pl-0.5 pr-2 py-0.5 rounded-full bg-[color:var(--sand-2)] text-[10px] font-medium ml-1">
                {j.avatar_file_id ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/profile/avatar/${j.avatar_file_id}`} alt="" className="w-4 h-4 rounded-full object-cover" />
                ) : (
                  <span className="w-4 h-4 rounded-full bg-[color:var(--ink-2)]/20" />
                )}
                @{j.username || j.name}
              </span>
            ))}
            {plan.joiners.length > 4 && <span className="text-[10px] ml-1">+{plan.joiners.length - 4}</span>}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-3 justify-end">
        <Button size="sm" variant="outline" onClick={() => sharePlan(plan)} data-testid={`share-${plan.id}`} className="pill">
          <Share2 className="w-4 h-4 mr-1.5" /> Share
        </Button>
        {isHost || isAdmin ? (
          <Button size="sm" variant="ghost" onClick={doDelete} data-testid={`delete-${plan.id}`} className="text-[color:var(--emergency)] hover:bg-[color:var(--emergency)]/10">
            <Trash2 className="w-4 h-4 mr-1.5" /> {isHost ? "Delete" : "Delete (admin)"}
          </Button>
        ) : isJoined ? (
          <Button size="sm" variant="outline" onClick={doLeave} data-testid={`leave-${plan.id}`} className="pill">Leave</Button>
        ) : (
          <Button size="sm" disabled={full} onClick={doJoin} data-testid={`join-${plan.id}`} className="pill bg-[color:var(--navy)] text-white hover:bg-[color:var(--navy-2)]">
            {full ? "Full" : "Join"}
          </Button>
        )}
      </div>
    </li>
  );
}

export default function SurfPlans({ beaches, onOpenAuth }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const qc = useQueryClient();
  const [tab, setTab] = useState("plans");
  const [composerOpen, setComposerOpen] = useState(false);

  const isAuthed = !!(user && user.id);

  const plansQ = useQuery({
    queryKey: ["plans"],
    queryFn: async () => (await http.get("/plans")).data,
    enabled: isAuthed,
    refetchInterval: 60_000,
  });
  const friendsQ = useQuery({
    queryKey: ["friends"],
    queryFn: async () => (await http.get("/friends")).data,
    enabled: isAuthed,
    refetchInterval: 60_000,
  });

  const refetchAll = () => { qc.invalidateQueries({ queryKey: ["plans"] }); qc.invalidateQueries({ queryKey: ["friends"] }); };

  const incomingBadge = friendsQ.data?.incoming?.length || 0;

  const plans = useMemo(() => plansQ.data || [], [plansQ.data]);
  const myPlans = useMemo(() => plans.filter((p) => p.host_id === user?.id), [plans, user]);
  const otherPlans = useMemo(() => plans.filter((p) => p.host_id !== user?.id), [plans, user]);

  return (
    <section data-testid="surf-plans" className="clay p-5 md:p-6">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <p className="eyebrow">{t("plans_eyebrow")}</p>
          <h3 className="text-xl md:text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <CalendarRange className="w-5 h-5 text-[color:var(--coral)]" />
            {t("plans_title")}
          </h3>
        </div>
        {isAuthed ? (
          <Button onClick={() => setComposerOpen(true)} data-testid="new-plan-btn" className="pill bg-[color:var(--coral)] text-white hover:bg-[color:var(--coral)]/90">
            <Plus className="w-4 h-4 mr-1.5" /> {t("plans_new")}
          </Button>
        ) : (
          <Button onClick={onOpenAuth} data-testid="plans-login-btn" className="pill bg-[color:var(--navy)] text-white hover:bg-[color:var(--navy-2)]">
            {t("plans_login")}
          </Button>
        )}
      </div>

      {isAuthed && (
        <>
          {/* Tabs */}
          <div className="inline-flex bg-[color:var(--sand-2)] rounded-full p-1 mb-4">
            <button
              onClick={() => setTab("plans")}
              data-testid="tab-plans"
              className={`pill px-4 py-1.5 text-sm font-semibold ${tab === "plans" ? "bg-white shadow-sm text-[color:var(--ink)]" : "text-[color:var(--ink-2)]"}`}
            >
              {t("plans_tab_plans")} {plans.length > 0 && <span className="ml-1 text-[10px] text-[color:var(--ink-2)]">({plans.length})</span>}
            </button>
            <button
              onClick={() => setTab("friends")}
              data-testid="tab-friends"
              className={`pill px-4 py-1.5 text-sm font-semibold relative ${tab === "friends" ? "bg-white shadow-sm text-[color:var(--ink)]" : "text-[color:var(--ink-2)]"}`}
            >
              {t("plans_tab_friends")}
              {incomingBadge > 0 && (
                <span data-testid="friends-badge" className="absolute -top-1 -right-1 bg-[color:var(--coral)] text-white text-[10px] h-5 min-w-[20px] rounded-full px-1 flex items-center justify-center font-semibold">
                  {incomingBadge}
                </span>
              )}
            </button>
          </div>

          {tab === "plans" && (
            <div className="space-y-5">
              {plansQ.isLoading && <p className="text-sm text-[color:var(--ink-2)]"><Loader2 className="inline w-4 h-4 animate-spin mr-1" /> {t("loading_surf")}</p>}
              {!plansQ.isLoading && plans.length === 0 && (
                <p className="text-sm text-[color:var(--ink-2)]">{t("plans_empty")}</p>
              )}
              {myPlans.length > 0 && (
                <div>
                  <p className="eyebrow mb-2">{t("plans_yours")}</p>
                  <ul className="space-y-3">
                    {myPlans.map((p) => <PlanCard key={p.id} plan={p} currentUser={user} onChanged={refetchAll} />)}
                  </ul>
                </div>
              )}
              {otherPlans.length > 0 && (
                <div>
                  <p className="eyebrow mb-2">{t("plans_others")}</p>
                  <ul className="space-y-3">
                    {otherPlans.map((p) => <PlanCard key={p.id} plan={p} currentUser={user} onChanged={refetchAll} />)}
                  </ul>
                </div>
              )}
            </div>
          )}

          {tab === "friends" && (
            <>
              {friendsQ.isLoading ? (
                <p className="text-sm text-[color:var(--ink-2)]"><Loader2 className="inline w-4 h-4 animate-spin mr-1" /> Loading…</p>
              ) : (
                <FriendsTab data={friendsQ.data || { friends: [], incoming: [], outgoing: [] }} refetch={refetchAll} />
              )}
            </>
          )}
        </>
      )}

      {composerOpen && (
        <PlanComposer
          beaches={beaches || []}
          friends={friendsQ.data?.friends || []}
          onCreated={() => { refetchAll(); toast.success("Plan created"); }}
          onClose={() => setComposerOpen(false)}
        />
      )}
    </section>
  );
}
